import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { saleKeys } from '../queries/keys';
import { salePaths } from '../queries/paths';

// TSS vite/03 — server-state hooks para ventas (api-reference/admin-sales.md). Venta
// POLIMÓRFICA (0058): enlaza una cría real (`calf_id`) y/o un cruce proyecto
// (`projected_cross_id`); el `kind` se deriva. El monto (`agreed_value`) es provisional
// (0047, migra a MBS). Permisos: clients:read/write.

// Drop empty filters so the cache key is stable and we don't send '' params.
function cleanFilters(filters = {}) {
    const out = {};
    for (const k of ['limit', 'offset', 'buyer', 'calf', 'projected_cross', 'status']) {
        const v = filters[k];
        if (v !== '' && v != null) out[k] = v;
    }
    return out;
}

// Página por defecto del listado (contrato: limit def 50 / máx 500).
const LIST_LIMIT = 50;

export function useSalesList(filters) {
    const params = cleanFilters(filters);
    return useQuery({
        queryKey: saleKeys.list(params),
        queryFn:  ({ signal }) => api.get(salePaths.list(), { params, signal }).then(r => r.data),
        placeholderData: (prev) => prev,
    });
}

// Listado paginado "cargar más" (offset). Páginas acumuladas en TanStack Query —
// vite/03 [RIGID]. Reemplaza el limit:500 + aviso de truncado del overview.
export function useSalesInfinite(filters, limit = LIST_LIMIT) {
    const params = cleanFilters(filters);
    return useInfiniteQuery({
        queryKey: saleKeys.infinite(params, limit),
        queryFn:  ({ pageParam, signal }) =>
            api.get(salePaths.list(), { params: { ...params, limit, offset: pageParam }, signal }).then(r => r.data),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const p = last?.pagination;
            if (!p) return undefined;
            const next = p.offset + p.limit;
            return next < p.total ? next : undefined;
        },
        placeholderData: (prev) => prev,
    });
}

export function useCreateSale() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(salePaths.list(), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: saleKeys.all }),
    });
}

export function useUpdateSale(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(salePaths.detail(id), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: saleKeys.all }),
    });
}

export function useDeleteSale() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(salePaths.detail(id)).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: saleKeys.all }),
    });
}

// Reconciliar una reserva de cruce (project_pending) → cría real cuando nace
// (PATCH /:id/reconcile { calf_id }). Único camino para tocar el target.
export function useReconcileSale(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (calfId) => api.patch(salePaths.reconcile(id), { calf_id: calfId }).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: saleKeys.all }),
    });
}

// --- Abonos de una venta (sub-recurso /:id/payments, Q-199) -----------------
// El historial de abonos del comprador (medio de pago = string libre). En la venta,
// `amount_paid`, `payments_count`, `last_payment_date` y `needs_recalculation` son
// DERIVADOS del historial (0001: el sistema no almacena saldo) → toda mutación de abono
// invalida TAMBIÉN saleKeys.all para refrescar el resumen de la venta en la lista.

export function useSalePayments(saleId) {
    return useQuery({
        queryKey: saleKeys.payments(saleId),
        queryFn:  ({ signal }) => api.get(salePaths.payments(saleId), { signal }).then(r => r.data),
        enabled:  saleId != null,
    });
}

// Refresca el historial del abono y el resumen derivado de la venta.
function invalidatePayments(qc, saleId) {
    qc.invalidateQueries({ queryKey: saleKeys.payments(saleId) });
    qc.invalidateQueries({ queryKey: saleKeys.all });
}

export function useCreateSalePayment(saleId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(salePaths.payments(saleId), payload).then(r => r.data),
        onSuccess:  () => invalidatePayments(qc, saleId),
    });
}

export function useUpdateSalePayment(saleId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ pid, ...payload }) => api.patch(salePaths.paymentDetail(saleId, pid), payload).then(r => r.data),
        onSuccess:  () => invalidatePayments(qc, saleId),
    });
}

export function useDeleteSalePayment(saleId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (pid) => api.delete(salePaths.paymentDetail(saleId, pid)).then(r => r.data),
        onSuccess:  () => invalidatePayments(qc, saleId),
    });
}
